<template>
	<div class="compose-wrapper">
		<h2 class="section-title">上传或粘贴图片</h2>

		<div
      class="upload-area"
      :class="{ 'upload-area--compact': uploader.length }"
      @click="triggerUpload"
    >
			<div class="upload-inner">
				<div class="upload-icon">📸</div>
				<div class="upload-text">
          {{ uploader.length ? '点击继续上传图片' : '点击上传图片' }}
        </div>
				<div class="upload-hint">支持 JPG、PNG 格式，可多选</div>
			</div>

      <!-- 上传后的缩略图直接显示在卡片内部底部 -->
      <div v-if="uploader.length" class="preview-grid">
        <div
          v-for="(item, index) in uploader"
          :key="index"
          class="preview-item"
        >
          <img :src="item.content" :alt="`图片${index + 1}`">
        </div>
      </div>
		</div>

		<!-- 隐藏的真实上传控件，沿用原来的逻辑，仅用于触发选择文件 -->
    <div class="hidden-upload">
      <uploader v-model="uploader" ref="imgs" multiple />
    </div>

		<div class="warning-tip" v-if="uploader.length">
			⚠️ 提示：图片过大或数量过多可能导致处理时间较长，甚至浏览器卡死。建议单张图片不超过 5MB，总数量不超过 20 张。
		</div>

		<div class="controls">
			<div class="control-group inline">
				<label class="control-label">宽度：</label>
				<input type="number" class="input" v-model="width">
				<label class="control-label inline-label">高度：</label>
				<input type="number" class="input" v-model="height">
			</div>

			<div class="control-group">
				<label class="control-label">
					帧延迟 <span class="value-display">{{ delay }}</span> ms
				</label>
				<input
					type="range"
					min="50"
					max="1000"
					step="50"
					v-model.number="delay"
					class="range"
				>
			</div>

			<div class="control-group">
				<label class="control-label">
					质量 <span class="value-display">{{ quality }}</span>
				</label>
				<input
					type="range"
					min="1"
					max="30"
					step="1"
					v-model.number="quality"
					class="range"
				>
			</div>
		</div>

		<div class="progress-container" v-if="isProcessing">
			<div class="progress-label">正在生成 GIF... {{ progressPercent }}%</div>
			<div class="progress-bar">
				<div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
			</div>
		</div>

		<div class="actions">
			<van-button
				type="primary"
				block
				:disabled="uploader.length < 2 || isProcessing"
				@click="toPreview"
			>
				生成 GIF 预览
			</van-button>
			<van-button
				type="primary"
				block
				class="download-btn"
				:disabled="uploader.length < 2 || isProcessing"
				@click="toDownload"
			>
				下载 GIF
			</van-button>
		</div>

		<img class="preview-img" v-show="gifImg" :src="gifImg" alt="">
	</div>
</template>

<script>
import { Field, Uploader } from "vant";
import GIF from "gif.js";
import { downloadFile, blobToBase64 } from './utils.js';

export default {
  components: {
    Field,
    Uploader
	},
  data () {
    return {
      uploader: [],
      gifImg: '',
      width: 0,
      height: 0,
      delay: 500,
      quality: 10,
      isProcessing: false,
      progressPercent: 0
		}
	},
	methods: {
    triggerUpload () {
      // 触发隐藏上传组件的文件选择
      if (this.$refs.imgs && this.$refs.imgs.$el) {
        const input = this.$refs.imgs.$el.querySelector('input[type="file"]')
        if (input) {
          input.click()
        }
      }
    },
    toPreview () {
      this.createGif(this.uploader.map(item => item.content))
    },
    toDownload () {
      this.createGif(this.uploader.map(item => item.content), true)
    },
    async createGif(imgs, needDownload = false) {
      if (!this.uploader.length) {
        return this.$toast('请上传图片');
      }
      if(this.uploader.length < 2) {
        return this.$toast('至少需要上传两张图片');
      }
      
      this.isProcessing = true
      this.progressPercent = 0
      
      const gif = new GIF({
        workers: 2,
        quality: +this.quality,
        workerScript: './static/gifjs/gif.worker.js'
      });
      
      // 监听进度事件
      gif.on('progress', (p) => {
        this.progressPercent = Math.round(p * 100)
      })
      
      gif.on('finished', async blob => {
        this.progressPercent = 100
        // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
        this.gifImg = await blobToBase64(blob)
        this.isProcessing = false
        if (!needDownload) return
        // 使用通用下载函数，支持微信小程序webview
        await downloadFile(this.gifImg, 'img.gif')
      })

      const canvas= document.createElement("canvas");
      const ctx = canvas.getContext('2d');
      const total = imgs.length
      
      for (let i = 0; i < imgs.length; i++) {
        canvas.width = +this.width
        canvas.height = +this.height
        canvas.delay = +this.delay
        await this.loadImg({
          gif,
          canvas,
          ctx,
          img: imgs[i]
        })
        // 更新进度：加载图片占70%，渲染占30%
        this.progressPercent = Math.round((i + 1) / total * 70)
      }
      gif.render()
    },
    loadImg ({gif, canvas, ctx, img } = config) {
      return new Promise(resolve => {
        const imgImage = new Image();
        imgImage.src = img;
        ctx.fillRect(0, 0, canvas.width, canvas.height);//铺底色
        imgImage.onload = (e) => {
          ctx.drawImage(imgImage, 0, 0, canvas.width, canvas.height);
          gif.addFrame(canvas, {copy: true, delay: canvas.delay});
          resolve()
        }
      })
    },
    setDefaultSize () {
      const img = document.createElement('img')
      img.setAttribute('src', this.uploader[0].content)
      img.setAttribute('style', "opacity: 0.01;")
      document.body.append(img)
      setTimeout(() => {
        const size = img.getBoundingClientRect()
        this.width = size.width
        this.height = size.height
        document.body.removeChild(img)
      }, 50)
    }
	},
	watch: {
    uploader(n, o) {
      if (n.length === 1 && !o.length) {
        this.setDefaultSize()
      }
    }
  },
	mounted () {
    document.addEventListener('paste', (event) => {
      if (event.clipboardData || event.originalEvent) {
        const clipboardData = (event.clipboardData || event.originalEvent.clipboardData);
        if (!clipboardData.items) return
        // todo粘贴板多内容还存在一些问题
        let cliDataArr = []
        for (let i = 0; i < clipboardData.items.length; i++) {
          cliDataArr.push(clipboardData.items[i])
        }
        const imgIndex = cliDataArr.findIndex(item => item.type.startsWith('image'))
        const videoIndex = cliDataArr.findIndex(item => item.type.startsWith('video'))
        if (imgIndex !== -1) {
          const blob = clipboardData.items[imgIndex].getAsFile()
          const render = new FileReader();
          render.onload = (evt) => {
            //输出base64编码
            var base64 = evt.target.result;
            this.uploader.push({
              file: clipboardData.files[imgIndex],
              content: base64,
              message: '',
              status: ''
            })
            if (this.uploader.length === 1) { // hack  没有触发 watch
              this.setDefaultSize()
            }
          }
          render.readAsDataURL(blob);
        }
      }
    })
	}
}
</script>

<style>
.compose-wrapper {
  padding: 8px 0;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2933;
}

.upload-area {
  border: 2px dashed #9fb3ff;
  border-radius: 16px;
  padding: 40px 16px;
  background: #f8f9ff;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
}

.upload-area:hover {
  border-color: #667eea;
  background: #f0f2ff;
}

.upload-area--compact {
  padding: 20px 16px;
}

.upload-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.upload-icon {
  font-size: 40px;
  margin-bottom: 4px;
}

.upload-text {
  font-size: 16px;
  color: #4f46e5;
  font-weight: 600;
}

.upload-hint {
  font-size: 12px;
  color: #9ca3af;
}

.hidden-upload {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
}

.controls {
  margin-top: 8px;
}

.control-group {
  margin-bottom: 16px;
}

.control-group.inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.control-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.inline-label {
  margin-left: 8px;
}

.input {
  width: 70px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 6px;
  font-size: 13px;
}

.range {
  width: 100%;
  margin-top: 8px;
}

.value-display {
  margin-left: 4px;
  color: #4f46e5;
  font-weight: 600;
}

.actions {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.download-btn {
  background: #e5edff !important;
  color: #4f46e5 !important;
  border: 1px solid #4f46e5 !important;
  height: 44px !important;
  line-height: 44px !important;
}

.preview-grid {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  margin-top: 16px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.preview-item {
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  background: white;
}

.preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-img {
  display: block;
  margin: 16px auto 0;
  max-width: 100%;
  max-height: 260px;
  border-radius: 12px;
  box-shadow: 0 6px 12px rgba(15, 23, 42, 0.15);
}

.warning-tip {
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #92400e;
  line-height: 1.5;
}

.progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

</style>